import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Route, Router } from '@angular/router';

import { BehaviorSubject, filter, forkJoin, from, map, mergeMap, switchMap, tap } from 'rxjs';

import { BaasAppState, BaasCommonApiClient, BarCacheStore, concatUrl } from '@lvflanders/bar';

import { BAAS_COMMON_API_CLIENT } from '../constants/internal-tokens.constants';
import { BAR_LOCALHOST } from '../constants/tokens.constants';

interface _AugmentedRoute extends Route {
    // used to define whether we already handled the route here
    _baasHandled: boolean;
    // Existing internal prop from Angular
    _loadedRoutes: Route[];
}

// internal key in app store
const KEY_ENDPOINTS = '_endpoints';

/** Internal class logging all routes of the app. */
@Injectable()
export class _BarRouteLogger implements OnDestroy {
    readonly #api = inject<BaasCommonApiClient>(BAAS_COMMON_API_CLIENT);
    readonly #localhost = inject<boolean>(BAR_LOCALHOST);
    readonly #router = inject(Router);
    readonly #state = inject(BaasAppState);
    readonly #appStore = inject(BarCacheStore);
    readonly #loggedEndpoints$$ = new BehaviorSubject<string[]>(undefined);
    readonly #destroyRef = inject(DestroyRef);

    ngOnDestroy(): void {
        this.#loggedEndpoints$$.complete();
    }

    init(): void {
        // behavior must be turned on and not in localhost modus
        const enabled = this.#state.config.baasStatic?.general?.endpointLoggingEnabled && !this.#localhost;
        if (!enabled) return;

        this.#router.events
            .pipe(
                filter((val) => val instanceof NavigationEnd),
                switchMap(() => {
                    // Check if data is available in the BehaviorSubject, otherwise, fetch from appStore
                    // NOTE this is to avoid having to access indexed db too much
                    if (this.#loggedEndpoints$$.getValue() === undefined) {
                        // also get the already logged endpoints from the storage
                        return from(this.#appStore.getItem(KEY_ENDPOINTS)).pipe(
                            tap((endpoints: string[]) => {
                                // Cache the fetched endpoints in the BehaviorSubject
                                this.#loggedEndpoints$$.next(endpoints);
                            })
                        );
                    } else {
                        return this.#loggedEndpoints$$.asObservable(); // Use cached data
                    }
                }),
                map((loggedEndpoints) => {
                    if (!loggedEndpoints) loggedEndpoints = [];
                    const endpointsToLog: string[] = [];
                    this.#collect(this.#router.config, endpointsToLog, loggedEndpoints);
                    return { endpoints: endpointsToLog, loggedEndpoints };
                }),
                filter((result) => result.endpoints.length > 0),
                mergeMap((result) => {
                    const remember$ = from(this.#appStore.setItem(KEY_ENDPOINTS, result.loggedEndpoints.concat(result.endpoints)));
                    return forkJoin([from(this.#api.saveEndpoints(this.#state.toepassing.code, this.#state.toepassing.versie, result.endpoints)), remember$]);
                }),
                takeUntilDestroyed(this.#destroyRef)
            )
            .subscribe();
    }

    #collect(routes: Route[], endpointsToLog: string[], loggedEndpoints: string[], parentPath: string = undefined): void {
        routes
            // don't collect framework routes
            .filter((route) => !route.data?._framework)
            .forEach((route: _AugmentedRoute) => {
                const fullPath = parentPath ? concatUrl(parentPath, route.path) : route.path;
                if (route.loadChildren) {
                    // check that lazy loaded module has already been loaded
                    if (route._loadedRoutes) {
                        // lazy loaded module, traverse the children
                        this.#collect(route._loadedRoutes, endpointsToLog, loggedEndpoints, fullPath);
                    }
                } else {
                    if (route.children?.length > 0) {
                        // this is a parent route, so traverse the children
                        this.#collect(route.children, endpointsToLog, loggedEndpoints, fullPath);
                    }
                    // define whether it is an end route
                    const endRoute = route.component || route.loadComponent || route.redirectTo;
                    // real route should have a component (but can still be a parent route)
                    if (!route._baasHandled && endRoute) {
                        const path = concatUrl(this.#state.location.appRoot.pathname, fullPath);
                        // chech that it has not been logged yet
                        if (loggedEndpoints.indexOf(path) == -1) {
                            // regular route, just log in case not handled yet
                            endpointsToLog.push(path);
                        }
                        // mark as handled in case leaf route
                        // note that a parent route could always have lazy loaded modules which have not been loaded yet
                        route._baasHandled = true;
                    }
                }
            });
    }
}
